
import { useEffect, useRef, useState, useCallback } from 'react';

export function useScrollAnimation(threshold = 0.1) {
  const [visibleItems, setVisibleItems] = useState<Set<number>>(new Set());
  const containerRef = useRef<HTMLDivElement>(null);

  const observerCallback = useCallback((entries: IntersectionObserverEntry[]) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const index = Number(entry.target.getAttribute('data-anim-index'));
        if (!isNaN(index)) {
          setVisibleItems((prev) => {
            const next = new Set(prev);
            next.add(index);
            return next;
          });
        }
      }
    });
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(observerCallback, {
      threshold,
      rootMargin: '0px 0px -40px 0px',
    });

    const container = containerRef.current;
    if (container) {
      const items = container.querySelectorAll('[data-anim-index]');
      items.forEach((item) => observer.observe(item));
    }

    return () => observer.disconnect();
  }, [observerCallback, threshold]);

  const getAnimationClass = (index: number) => {
    const isVisible = visibleItems.has(index);
    return isVisible
      ? 'opacity-100 translate-y-0 scale-100'
      : 'opacity-0 translate-y-8 scale-95';
  };

  const getAnimationStyle = (index: number) => ({
    transitionDelay: `${(index % 4) * 100}ms`,
  });

  return { containerRef, getAnimationClass, getAnimationStyle };
}
